/** @jsx jsx */
import { Flex, FormGroup, InfoBox } from "@eworkit/iris";
import { Formik, FormikProps } from "formik";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { FormikCvFileUpload } from "@/components/Files/upload/CvFileUpload";
import { FileUploadStateItem } from "@/components/Files/upload/fileUploadReducer";
import { VALIDATION_TYPES } from "@/components/Formik";
import { Attachment } from "@/data/attachments";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { FormikForm, isErrorVisible } from "@/formikComponents";

import { profilePartialUpdaterBuilder } from "../profilePartialUpdaterBuilder";
import { IProfileFormikProps } from "../types";

export const validationCvSectionSchema = Yup.object().shape({
  cvs: Yup.array()
    .test({
      message: VALIDATION_TYPES.UPLOAD_PROGRESS,
      test: (value: FileUploadStateItem<Attachment>[]) => value.every(i => i.progress === 100),
    })
    .test({
      message: VALIDATION_TYPES.UPLOAD_ERRORS,
      test: (value: FileUploadStateItem<Attachment>[]) => value.every(i => !i.error),
    })
    .test({
      message: VALIDATION_TYPES.NO_DEFAULT_CV,
      test: (value: FileUploadStateItem<Attachment>[]) =>
        !value.length ? true : !!value.find(x => x.attachment?.defaultFile === true),
    }),
});

export interface ICvFormSectionValues {
  cvs: FileUploadStateItem<Attachment>[];
}

interface ICvFormSectionProps {
  form: FormikProps<ICvFormSectionValues>;
}

interface ICvFormComponentProps extends ICvFormSectionProps {
  onChange?: (cvs: FileUploadStateItem<Attachment>[]) => void;
}

interface ICvFormProps extends ICvFormComponentProps, IEditableSectionRenderProps {
  source: IConsultantProfile;
  onCancel: () => void;
}

export const CvFormComponent: FC<ICvFormComponentProps> = ({ form, onChange }) => {
  const { t } = useTranslation("session");

  return (
    <Flex flexDirection="column" gap="md">
      {isErrorVisible(form, "cvs") && (
        <InfoBox text={t(`common:VALIDATION_MESSAGES.${form.errors.cvs}`)} variant="danger" />
      )}

      <FormGroup>
        <FormikCvFileUpload
          name="cvs"
          onChange={onChange}
          cvs={form.values.cvs.filter(i => i.attachment).map(i => i.attachment!)}
        />
      </FormGroup>
    </Flex>
  );
};

export const CvForm: React.FunctionComponent<ICvFormProps> = ({ form, editableProps, onChange }) => {
  useEditableSubmit(form, editableProps);

  const handleChange = (value: FileUploadStateItem<Attachment>[]) => {
    if (onChange) {
      onChange(value);
    }
  };

  return (
    <FormikForm>
      <CvFormComponent form={form} onChange={handleChange} />
    </FormikForm>
  );
};

export interface ICvFormikProps extends IProfileFormikProps<ICvFormSectionValues>, ICvFormProps {}

export const CvFormik: FC<ICvFormikProps> = React.memo(({ initialValues, validationSchema, onSubmit, ...rest }) => (
  <Formik<ICvFormSectionValues> initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
    {form => <CvForm {...rest} form={form} />}
  </Formik>
));

export const ConsultantProfileCvEdit = profilePartialUpdaterBuilder<ICvFormSectionValues, ICvFormikProps>(CvFormik, {
  validationSchema: validationCvSectionSchema,
  messengerContext: "",
  mapValues: values => ({
    ...values,
    cvs: values.cvs.filter(i => i.attachment).map(i => i.attachment!),
  }),
});
